import * as React from "react";
import { createPortal } from "react-dom";

export interface IRefPortalProps {
  selector: string;
  key?: string;
  children: React.ReactNode | React.ReactNode[];
}

export default function RefPortal(props: IRefPortalProps) {
  const [element, setElement] = React.useState<HTMLElement | null>(
    document.querySelector(props.selector) as HTMLElement | null,
  );
  const observerRef = React.useRef<MutationObserver | null>(null);
  const intervalRef = React.useRef<number>();
  React.useEffect(() => {
    if (!element) return;

    const observer = new MutationObserver((mutationsList) => {
      for (const mutation of mutationsList) {
        if (mutation.type !== "childList") continue;
        for (const removedNode of mutation.removedNodes)
          if (element.isEqualNode(removedNode)) {
            setElement(null);
            return;
          }
      }
    });
    observer.observe(element.parentNode!, { childList: true });
    observerRef.current = observer;
    return () => observer.disconnect();
  }, [element]);
  React.useEffect(() => {
    if (element) return;
    const id = window.setInterval(() => {
      const el = document.querySelector(props.selector) as HTMLElement | null;
      if (el) setElement(el);
    }, 100);
    intervalRef.current = id;
    return () => clearInterval(id);
  }, [element, props.selector]);
  React.useEffect(() => {
    if (element && !element.isConnected)
      setElement(document.querySelector(props.selector) as HTMLElement | null);
  });

  React.useEffect(() => {
    return () => {
      observerRef.current?.disconnect();
      if (intervalRef.current) clearInterval(intervalRef.current);
    };
  }, []);

  if (!element) return null;

  return createPortal(props.children, element, props.selector);
}
